// highlight.js — repérage du mot-vedette (et de ses formes fléchies) dans une
// phrase d'exemple, pour le surligner.
//
// Pur, sans React : renvoie des plages [début, fin] sur la chaîne d'origine
// (le rendu <strong> est fait par le composant). Conçu pour le russe, très
// flexionnel (геро́й → геро́ем, у́тро → у́тром), tout en gérant les langues cibles
// (pluriels, accents, ё/е). Aucune dépendance externe.

// Mots-outils ignorés UNIQUEMENT quand un sens est donné en plusieurs mots
// (ex. « La mer » → on surligne « mer », pas « la »). Formes déjà normalisées
// (minuscules, sans accent — voir normalize).
const STOPWORDS = new Set([
  // FR
  'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'd', 'l', 'au', 'aux', 'et', 'en', 'ne',
  // EN
  'the', 'a', 'an', 'to', 'of', 'and', 'or',
  // DE
  'der', 'die', 'das', 'den', 'dem', 'ein', 'eine', 'einen', 'einem', 'zu', 'und',
  // ES
  'el', 'los', 'las', 'del', 'y',
  // IT
  'il', 'lo', 'i', 'gli', 'di', 'e',
]);

// Accents latins des 5 langues (ensemble fini). On ne passe pas par NFD car il
// décomposerait aussi le cyrillique й (→ и + brève) et casserait le russe.
const LATIN_ACCENTS = {
  à: 'a', â: 'a', ä: 'a', á: 'a', ã: 'a', å: 'a', ç: 'c',
  é: 'e', è: 'e', ê: 'e', ë: 'e', í: 'i', î: 'i', ï: 'i', ì: 'i',
  ñ: 'n', ó: 'o', ô: 'o', ö: 'o', ò: 'o', õ: 'o',
  ú: 'u', û: 'u', ü: 'u', ù: 'u', ý: 'y', ÿ: 'y', ß: 'ss', œ: 'oe', æ: 'ae',
};
const ACCENT_RE = new RegExp('[' + Object.keys(LATIN_ACCENTS).join('') + ']', 'g');

// Désinences russes (déjà normalisées). Le strip retire la PLUS LONGUE désinence
// présente sans réduire le radical sous MIN_STEM lettres. On évite volontairement
// les consonnes finales seules (-л, -н) qui appartiennent souvent au radical
// (стол, стул…) et créeraient des collisions (стол ↔ стоит).
const RU_ENDINGS = [
  'иями', 'ями', 'ами', 'иях', 'иям', 'ого', 'его', 'ому', 'ему', 'ыми', 'ими',
  'ешь', 'ишь', 'ете', 'ите', 'ься', 'ась', 'ось', 'ись', 'ться', 'тся',
  'ах', 'ях', 'ам', 'ям', 'ов', 'ев', 'ой', 'ей', 'ою', 'ею', 'ом', 'ем',
  'ый', 'ий', 'ая', 'яя', 'ое', 'ее', 'ые', 'ие', 'их', 'ых', 'ют', 'ут',
  'ат', 'ят', 'им', 'ет', 'ит', 'ла', 'ло', 'ли', 'на', 'но', 'ны', 'ть',
  'ти', 'ся', 'сь', 'у', 'ю', 'а', 'я', 'о', 'е', 'и', 'ы', 'й', 'ь',
].sort((a, b) => b.length - a.length);

// Désinences latines : on ne retire que le pluriel régulier ; le reste (‑e/‑en
// allemand, ‑i/‑e italien…) est rattrapé par le repli sur préfixe commun.
const LAT_ENDINGS = ['es', 's'];

const MIN_STEM = 3; // longueur minimale d'un radical après strip

// Minuscule + ё→е + accents latins aplatis (é→e, ü→u, ñ→n…). La même
// normalisation est appliquée aux candidats et aux jetons : seule la cohérence
// des deux côtés compte.
export const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(ACCENT_RE, (c) => LATIN_ACCENTS[c])
    .trim();

const isCyrillic = (w) => /[а-я]/.test(w);

const stripEnding = (w, endings) => {
  for (const end of endings) {
    if (w.length - end.length >= MIN_STEM && w.endsWith(end)) {
      return w.slice(0, -end.length);
    }
  }
  return w;
};

const stem = (w) => stripEnding(w, isCyrillic(w) ? RU_ENDINGS : LAT_ENDINGS);

const commonPrefixLen = (a, b) => {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return i;
};

// Un jeton (déjà normalisé) correspond-il au candidat (déjà normalisé) ?
const tokenMatches = (tok, cand) => {
  if (!tok || !cand) return false;
  if (tok === cand) return true;
  if (cand.length <= 2) return false; // mots très courts : exact uniquement (не, об, de…)

  const sc = stem(cand);
  if (sc.length >= MIN_STEM && sc === stem(tok)) return true;

  // Repli : long préfixe commun couvrant ≥ 60 % des deux mots
  // (gère les noms à mutation : Hund/Hunde, fragola/fragole, герой/героем…).
  const lcp = commonPrefixLen(tok, cand);
  return lcp >= 4 && lcp >= 0.6 * cand.length && lcp >= 0.6 * tok.length;
};

// Termes à rechercher, extraits du mot-vedette ou de la glose.
// « Brujería / Hechicería » → ['brujeria', 'hechiceria']
// « La mer »               → ['mer']   (l'article est filtré)
// « герой »                → ['герой']
export const extractCandidates = (word) => {
  let terms = normalize(word).split(/[^\p{L}\p{M}]+/u).filter(Boolean);
  if (terms.length > 1) {
    terms = terms.filter((w) => w.length >= MIN_STEM && !STOPWORDS.has(w));
  }
  if (terms.length === 0) {
    const single = normalize(word);
    return single ? [single] : [];
  }
  return terms;
};

// Plages [début, fin] (sur la chaîne d'origine) des jetons à surligner.
export const findHighlightRanges = (sentence, word) => {
  if (!sentence || !word) return [];
  const cands = extractCandidates(word);
  if (!cands.length) return [];

  const ranges = [];
  const re = /[\p{L}\p{M}]+/gu;
  let m;
  while ((m = re.exec(sentence)) !== null) {
    const tokNorm = normalize(m[0]);
    if (cands.some((c) => tokenMatches(tokNorm, c))) {
      ranges.push([m.index, m.index + m[0].length]);
    }
  }
  return ranges;
};
